import { useCallback, useMemo } from 'react';
import { createSelector } from '@reduxjs/toolkit';

import type {
  IResourceAnnouncementItem,
  IResourceAnnouncementItemWithClient,
  IResourceDocumentItem,
  IResourceDocumentItemWithClient,
  IResourceItemType,
  IResourceItemWithClientType,
  IResourceLinkItem,
  IResourceLinkItemWithClient,
} from '@/api/types';
import type { ITreeFolder } from '@/components/common';
import type { IResourcesState } from '@/store/types';

import { getPinnedAnnouncements, mapToResourceWithClient } from '@/helpers';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { selectFoldersList } from '@/store/selectors/resourcesSelectors';
import { getFavoritesList, getTree } from '@/store/slices/resourcesSlice';
import { UserType } from '@/store/enums';

interface IProps {
  allItems: IResourceItemType[];
  recentlyCreatedItems: IResourceItemWithClientType[];
  recentlyPublishedItems: IResourceItemWithClientType[];
  foldersList: ITreeFolder[];
  pinnedAnnouncements: IResourceAnnouncementItemWithClient[];
  initFolderTree: (userType: UserType, currentClientId?: number) => void;
  mapFavoriteItemsWithClient: (favorites: IResourcesState['favorites']) => IResourceItemWithClientType[];
}

const resourcesHookSelector = createSelector(selectFoldersList, foldersList => ({
  foldersList,
  pinnedAnnouncements: foldersList ? getPinnedAnnouncements(foldersList) : [],
}));

const useResources = (): IProps => {
  const { foldersList, pinnedAnnouncements } = useAppSelector(resourcesHookSelector);

  const dispatch = useAppDispatch();

  const initFolderTree = useCallback<IProps['initFolderTree']>(
    userType => {
      if (userType === UserType.agent) {
        dispatch(getTree());
        dispatch(getFavoritesList());
      }

      if (userType !== UserType.agent) {
        dispatch(getTree());
      }
    },
    [dispatch],
  );

  const allItems = useMemo(() => {
    if (foldersList === null) return [];

    const announcements: IResourceAnnouncementItem[] = [];
    const docs: IResourceDocumentItem[] = [];
    const links: IResourceLinkItem[] = [];

    foldersList.forEach(folder => {
      JSON.stringify(folder, (key, value) => {
        if (key === 'announcements') announcements.push(value);
        else if (key === 'docs') docs.push(value);
        else if (key === 'links') links.push(value);
        return value;
      });
    });

    return [...announcements, ...docs, ...links].flat();
  }, [foldersList]);

  const mapFavoriteItemsWithClient = useCallback<IProps['mapFavoriteItemsWithClient']>(
    favorites => {
      if (favorites === null) return [];
      if (foldersList === null) return [];

      const allFavorites = Object.values(favorites).flat();

      const favItems = foldersList.flatMap(client => {
        const favAnnouncements = client.announcement.flatMap(folder =>
          folder.announcements
            .filter(announcement => allFavorites.includes(announcement.announcementId))
            .map<IResourceAnnouncementItemWithClient>(mapToResourceWithClient(client)),
        );

        const favDocuments = client.document.flatMap(folder =>
          folder.docs
            .filter(doc => allFavorites.includes(doc.documentId))
            .map<IResourceDocumentItemWithClient>(mapToResourceWithClient(client)),
        );

        const favLinks = client.link.flatMap(folder =>
          folder.links
            .filter(link => allFavorites.includes(link.linkId))
            .map<IResourceLinkItemWithClient>(mapToResourceWithClient(client)),
        );

        return [...favAnnouncements, ...favDocuments, ...favLinks];
      });

      return favItems;
    },
    [foldersList],
  );

  const itemsWithClient = useMemo(() => {
    if (foldersList === null) return [];

    const itemsWithClient = foldersList.flatMap(client => {
      const announcementsWithClient = client.announcement.flatMap(folder =>
        folder.announcements.map<IResourceAnnouncementItemWithClient>(mapToResourceWithClient(client)),
      );

      const documentsWithClient = client.document.flatMap(folder =>
        folder.docs.map<IResourceDocumentItemWithClient>(mapToResourceWithClient(client)),
      );

      const linksWithClient = client.link.flatMap(folder =>
        folder.links.map<IResourceLinkItemWithClient>(mapToResourceWithClient(client)),
      );

      return [...announcementsWithClient, ...documentsWithClient, ...linksWithClient];
    });

    return itemsWithClient;
  }, [foldersList]);

  const recentlyCreatedItems = useMemo(() => {
    return itemsWithClient.sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
  }, [itemsWithClient]);

  const recentlyPublishedItems = useMemo(() => {
    return itemsWithClient.sort((a, b) => new Date(b.dateStart).getTime() - new Date(a.dateStart).getTime());
  }, [itemsWithClient]);

  return {
    allItems,
    recentlyCreatedItems,
    recentlyPublishedItems,
    foldersList: foldersList || [],
    pinnedAnnouncements,
    initFolderTree,
    mapFavoriteItemsWithClient,
  };
};

export { useResources };
